/**
 * Custom hook to determine if V2 GraphQL fields should be used
 *
 * This hook wraps the LaunchDarkly feature flag for V2 GraphQL fields.
 * It returns false by default if the flag is not set or LaunchDarkly is unavailable.
 *
 * Note: This hook will re-render when LaunchDarkly flags load. Initially it returns
 * FALLBACK_FLAGS (false), then updates when actual LD flags are available.
 *
 * @returns {boolean} True if V2 fields are enabled via feature flag, false otherwise
 *
 * @example
 * const isV2Fields = useGQLv2Fields();
 * const settingsQuery = isV2Fields ? GET_SETTINGS_V2 : GET_SETTINGS;
 *
 * @example
 * const isV2Fields = useGQLv2Fields();
 * const userSettings = parseUserSettingsData(settingsData, isV2Fields);
 */

import { useMemo } from 'react';
import useLdFlags from './useLdFlags';
import { FLAG_NAMES } from '../constants';

const useGQLv2Fields = () => {
  const flags = useLdFlags();

  // Memoize the result to avoid unnecessary re-renders, but it will update
  // when flags change (i.e., when LD loads or returns FALLBACK_FLAGS)
  return useMemo(() => {
    return Boolean(flags?.[FLAG_NAMES.UI_USERPROFILE_V2_FIELDS]);
  }, [flags]);
};

export default useGQLv2Fields;
